import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check, CornerUpLeft, Circle, CircleDot } from 'lucide-react'
import type { NtProposal, NtExemplar, NtTerm, NtProjectBlock, NtProof, NtHost, StudentDoc } from '../../core/types'
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage'
import { storage } from '../../core/firebase'
import { useAuth } from '../../core/auth'
import {
  PROJECT_TYPES, useExemplars, useProposalEvents, useSeminarProposals, useSeminarStudents,
  useMyProposal, moveProposal, saveProposal, deleteDraft, proposalWhere, type ProposalContent,
  projectSlots, projectPlanGaps, linkPartners, DAY_LABELS, useSeminarSelections, ranksTotal, FRIDAY_WHERE, proposalTypes, needsProof, BLANK_HOST, hostGaps, hostSummary,
} from './model'
import { studentProgress, followUps } from './dashboard'
import { mergeSelections } from './selections'
import { useNorseTerm } from './NorseTermData'
import { Text, Area } from '../../ui/Field'
import { Empty, ErrorState, Loading } from '../../ui/States'

/**
 * Independent projects — a student spends the week outside a course.
 *
 * Three views of one record, and the record is the same in all three: the
 * student writes and revises it, their seminar teacher checks it off, admin or
 * office gives final approval. Anyone along the way can send it back with a
 * note, and every step lands in the history underneath, so "what did they ask
 * me to change?" always has an answer.
 *
 * The exemplars sit next to every view on purpose. Every weak proposal fails
 * the same three ways — no named host who has agreed, a plan that does not
 * change across the week, and nothing that exists on Friday — and the
 * fastest way to say that is to show a strong one beside a weak one.
 */

type Me = { email: string; name: string }

/* ---------------- admin and office ---------------- */

export function Projects({ proposals, canDecide, me }: {
  proposals: NtProposal[]
  canDecide: boolean
  me: Me
}) {
  const ready = proposals.filter((p) => p.status === 'checked')
  const withSeminar = proposals.filter((p) => p.status === 'submitted')
  const returned = proposals.filter((p) => p.status === 'returned')
  const approved = proposals.filter((p) => p.status === 'approved')
  const drafting = proposals.filter((p) => p.status === 'draft').length

  return (
    <>
      <Section title="Ready for approval" items={ready}
        empty="Nothing checked off and waiting. Seminar teachers check proposals first."
        render={(p) => <ProposalCard key={p.id} p={p} all={proposals} viewer="decider" me={me} canAct={canDecide} />} />
      {withSeminar.length > 0 && (
        <Section title="Still with the seminar teacher" items={withSeminar}
          hint="You can approve one of these directly if it has stalled."
          render={(p) => <ProposalCard key={p.id} p={p} all={proposals} viewer="decider" me={me} canAct={canDecide} collapsed />} />
      )}
      {returned.length > 0 && (
        <Section title="Sent back, waiting on the student" items={returned}
          render={(p) => <ProposalCard key={p.id} p={p} all={proposals} viewer="decider" me={me} canAct={false} collapsed />} />
      )}
      {approved.length > 0 && (
        <Section title="Approved" items={approved}
          render={(p) => <ProposalCard key={p.id} p={p} all={proposals} viewer="decider" me={me} canAct={canDecide} collapsed />} />
      )}
      {drafting > 0 && (
        <p className="opt__hint">{drafting} {drafting === 1 ? 'student is' : 'students are'} still drafting. Nobody sees a draft until it is submitted.</p>
      )}
    </>
  )
}

/* ---------------- seminar teachers ---------------- */

export function SeminarQueue({ termId, me }: { termId: string; me: Me }) {
  const proposals = useSeminarProposals(termId, me.email)
  const students = useSeminarStudents(me.email)
  const waiting = proposals.filter((p) => p.status === 'submitted')
  const rest = proposals.filter((p) => p.status !== 'submitted' && p.status !== 'draft')

  return (
    <>
      <SeminarRoster termId={termId} me={me} students={students} proposals={proposals} />
      <h2 className="work__heading" style={{ marginTop: 'var(--s-5)' }}>Independent project proposals</h2>
      <p className="muted-note" style={{ marginTop: 0 }}>
        You check your seminar students&apos; proposals first; admin gives final approval.
      </p>
      <Section title="Waiting on you" items={waiting}
        empty="Nothing waiting. Proposals your seminar students submit land here."
        render={(p) => <ProposalCard key={p.id} p={p} viewer="seminar" me={me} canAct />} />
      {rest.length > 0 && (
        <Section title="Already through you" items={rest}
          render={(p) => <ProposalCard key={p.id} p={p} viewer="seminar" me={me} canAct={false} collapsed />} />
      )}
    </>
  )
}

/**
 * Every student in this seminar and where they are: course selections, the
 * project proposal, the artifact course. Whoever still has something to
 * finish comes first, in words a teacher can say to them, so the seminar
 * teacher knows who to call in.
 */
function SeminarRoster({ termId, me, students, proposals }: {
  termId: string; me: Me; students: StudentDoc[]; proposals: NtProposal[]
}) {
  const { term, schedules } = useNorseTerm()
  const selections = useSeminarSelections(termId, me.email)
  const [onlyNeeds, setOnlyNeeds] = useState(true)
  const required = ranksTotal(term)
  const rows = studentProgress(students, mergeSelections([], selections), proposals, null, required, new Date(),
    schedules.filter((x) => students.some((st) => st.studentId === x.studentId)))
    .map((r) => ({ ...r, needs: followUps(r, required) }))
    .sort((a, b) => b.needs.length - a.needs.length || a.name.localeCompare(b.name))
  const needing = rows.filter((r) => r.needs.length).length
  const shown = onlyNeeds ? rows.filter((r) => r.needs.length) : rows
  const sel = (r: (typeof rows)[number]) => (r.ranked ? '✓ Submitted' : r.picked ? `${r.picked} of ${required} picked` : 'Not started')
  const proj = (r: (typeof rows)[number]) => (r.grade <= 9 ? 'n/a (9th grade)' : PROJECT_WORDS[r.project] ?? r.project)

  return (
    <section aria-label="My seminar students">
      <div className="steps__head" style={{ marginTop: 'var(--s-2)' }}>
        <h2 className="ov__h" style={{ margin: 0 }}>My seminar students</h2>
        <span className="work__sub">
          {students.length} students · {needing ? `${needing} still need to finish something` : 'everyone is up to date'}
        </span>
      </div>
      {students.length === 0 ? (
        <Empty title="No seminar students yet" body="Your seminar list comes from the student roster. Once it is synced, your students appear here." />
      ) : (
        <>
          <div className="chips" style={{ margin: 'var(--s-2) 0' }}>
            <button className={`chip${onlyNeeds ? ' is-on' : ''}`} onClick={() => setOnlyNeeds(true)}>Need to finish something ({needing})</button>
            <button className={`chip${!onlyNeeds ? ' is-on' : ''}`} onClick={() => setOnlyNeeds(false)}>Everyone ({rows.length})</button>
          </div>
          {shown.length === 0 ? <p className="muted-note">Nobody is behind. Nice.</p> : (
            <>
              <table className="table">
                <thead><tr><th>Student</th><th>Grade</th><th>Course selections</th><th>Project proposal</th><th>Still to do</th></tr></thead>
                <tbody>
                  {shown.map((r) => (
                    <tr key={r.email} className={r.needs.length ? 'is-flag' : ''}>
                      <td style={{ fontWeight: 610 }}>{r.name}</td>
                      <td>{r.grade}</td>
                      <td className={r.ranked ? '' : 'is-muted'}>{sel(r)}</td>
                      <td>{proj(r)}</td>
                      <td>{r.needs.join(' · ') || '✓ Nothing'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="work cards">
                {shown.map((r) => (
                  <li key={r.email} className="work__row" style={{ display: 'block' }}>
                    <div style={{ fontWeight: 610 }}>{r.name} <span className="work__sub">· grade {r.grade}</span></div>
                    <div className="work__sub">Course selections: {sel(r)} · Project: {proj(r)}</div>
                    {r.needs.length > 0 && <div className="work__status">{r.needs.join(' · ')}</div>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  )
}

const PROJECT_WORDS: Record<string, string> = {
  none: 'None (optional)', draft: 'Draft, not submitted', submitted: 'Waiting on you', checked: 'With admin',
  approved: '✓ Approved', returned: 'Sent back for revision',
}

/* ---------------- the student ---------------- */

export function StudentProject({ term, me }: { term: NtTerm; me: StudentDoc }) {
  const p = useMyProposal(term.id, me.email)
  const [editing, setEditing] = useState(false)

  if (p === null) return <Loading label="Loading your project" />

  const mayEdit = !p || p.status === 'draft' || p.status === 'returned'
  const showForm = !p ? editing : mayEdit && (editing || p.status === 'draft')

  return (
    <>
      {!p && !editing && (
        <section className="card">
          <p style={{ margin: '0 0 var(--s-2)', fontSize: 'var(--t-sm)', lineHeight: 1.5 }}>
            Instead of two courses, you can spend Norse Term on a project of your own: shadowing a job,
            a college visit, service, or something you are passionate about. Most students take courses;
            this is for the ones with a specific plan and an adult who has agreed to host them.
          </p>
          <p className="opt__hint" style={{ margin: '0 0 var(--s-3)' }}>
            {me.seminarTeacherName || 'Your seminar teacher'} checks it first, then an administrator approves it.
            {term.deadlines.proposals && <> Due {fmtDate(term.deadlines.proposals)}.</>}
          </p>
          <button className="btn" onClick={() => setEditing(true)}>Start a proposal</button>
        </section>
      )}

      {p && <Track status={p.status} />}

      {p?.status === 'returned' && p.feedback && (
        <div className="notice notice--warning" role="status">
          <CornerUpLeft size={15} aria-hidden="true" />
          <div><strong>Sent back:</strong> {p.feedback}</div>
        </div>
      )}

      {showForm ? (
        <ProposalForm term={term} me={me} existing={p ?? undefined} onDone={() => setEditing(false)} />
      ) : p ? (
        <>
          <ProposalCard p={p} viewer="student" me={{ email: me.email, name: me.displayName }} canAct={false} />
          {mayEdit && <button className="btn" onClick={() => setEditing(true)}>Revise it</button>}
        </>
      ) : null}

      <Exemplars />
    </>
  )
}

function ProposalForm({ term, me, existing, onDone }: {
  term: NtTerm
  me: StudentDoc
  existing: NtProposal | undefined
  onDone: () => void
}) {
  const [f, setF] = useState<ProposalContent>({
    type: existing?.type ?? 'shadow', types: existing ? proposalTypes(existing) : ['shadow'], title: existing?.title ?? '',
    detail: existing?.detail ?? '', host: existing?.host ?? '',
    plan: existing?.plan ?? [], artifact: existing?.artifact ?? '', proofs: existing?.proofs ?? [], partnersText: existing?.partnersText ?? '',
    // Proposals saved before hosts were split up keep their files on the first card.
    hosts: existing?.hosts?.length ? existing.hosts : [{ ...BLANK_HOST(), proofs: existing?.proofs ?? [] }],
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = <K extends keyof ProposalContent>(k: K, v: ProposalContent[K]) => setF((x) => ({ ...x, [k]: v }))
  const gaps = projectPlanGaps(f.plan, term)
  const hosts = f.hosts ?? []
  const proofRequired = needsProof(f.types)
  const hostsDone = hosts.length > 0 && hosts.every((h) => hostGaps(h, proofRequired).length === 0)
  const complete = (f.types ?? []).length > 0 && hostsDone && f.title.trim() && f.detail.trim() && (f.artifact ?? '').trim() && gaps === 0

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true); setErr(null)
    try { await fn(); onDone() }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }
  const content = (): ProposalContent => {
    const trimmed = hosts.map((h) => ({ ...h, org: h.org.trim(), name: h.name.trim(), role: h.role.trim(),
      phone: h.phone.trim(), email: h.email.trim(), address: h.address.trim() }))
    const kept = trimmed.filter((h) => h.org || h.name || h.phone || h.email || h.address || h.proofs.length)
    return { ...f, title: f.title.trim(), detail: f.detail.trim(), partnersText: (f.partnersText ?? '').trim(),
    hosts: kept, host: hostSummary(kept).slice(0, 500), proofs: kept.flatMap((h) => h.proofs),
    artifact: (f.artifact ?? '').trim(),
    // Friday is on campus for everyone: its place is always school.
    plan: (f.plan ?? []).map((b) => ({ ...b, what: b.what.trim(), where: b.day === 3 ? FRIDAY_WHERE : b.where.trim() }))
      .filter((b) => b.what || (b.day !== 3 && b.where)) }
  }
  const save = run(() => saveProposal(term.id, me, content(), existing))
  const submit = run(async () => {
    await saveProposal(term.id, me, content(), existing)
    await moveProposal(`${term.id}_${me.email}`, { status: 'submitted' },
      { byEmail: me.email, byName: me.displayName, action: 'submitted' })
  })

  return (
    <div className="pform">
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <FormStep n={1} title="Your project" hint="What kind of project it is, what to call it, and what you'll do.">
      <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s-4)' }}>
        <legend style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)', padding: 0 }}>Kind of project: pick all that apply</legend>
        <p className="opt__hint" style={{ margin: '2px 0 0' }}>Your week can mix them, e.g. a job shadow Tuesday and college visits Wednesday and Thursday. Say which day is which in your week plan below.</p>
        <div style={{ display: 'grid', gap: 4, marginTop: 'var(--s-2)' }}>
          {PROJECT_TYPES.map((t) => (
            <label key={t.k} className="pick" style={{ alignItems: 'flex-start', paddingTop: 'var(--s-2)' }}>
              <input type="checkbox" checked={(f.types ?? []).includes(t.k)} style={{ marginTop: 3 }}
                onChange={(e) => {
                  const next = e.target.checked ? [...(f.types ?? []), t.k] : (f.types ?? []).filter((x) => x !== t.k)
                  const ordered = PROJECT_TYPES.map((x) => x.k).filter((k) => next.includes(k))
                  setF((x) => ({ ...x, types: ordered, type: ordered[0] ?? x.type }))
                }} />
              <span className="pick__label">
                <span style={{ fontWeight: 600 }}>{t.label}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 2 }}>{t.blurb}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <Text label="Title" value={f.title} onChange={(v) => set('title', v)} required
        placeholder="Four days with Story County EMS" />
      <Area label="Overview" value={f.detail} onChange={(v) => set('detail', v)} required
        placeholder="What the project is, why you want to do it, and what you hope to learn." />
      <Text label="Working with another student? (optional)" value={f.partnersText ?? ''} onChange={(v) => set('partnersText', v)}
        placeholder="Their full name, e.g. Ella Gjerde. Each of you sends your own proposal."
        help="Each partner submits their own proposal; the office joins them into one project." />
      </FormStep>

      <FormStep n={2} title="Who has agreed to host you?" required
        hint="One card for each place you'll be. The most common reason a proposal is sent back is not having this yet.">
      <HostsEditor termId={term.id} email={me.email} hosts={hosts} proofRequired={proofRequired}
        legacy={!existing?.hosts?.length ? existing?.host : undefined}
        onChange={(next) => set('hosts', next)} />
      </FormStep>

      <FormStep n={3} title="Your week, block by block" required
        hint="The same blocks as the school day. Tuesday to Thursday you're expected to be at your site just like a school day, so say what you'll be doing and where for each one. Wednesday is an early out. Friday everyone is on campus, getting their artifact ready for the open house.">
        <ProjectPlan term={term} plan={f.plan ?? []} hosts={hosts} onChange={(plan) => set('plan', plan)} />
      </FormStep>

      <FormStep n={4} title="Your open-house artifact" required
        hint="What will you bring to the open house to show what you learned?">
        <Area label="Your artifact" value={f.artifact ?? ''} onChange={(v) => set('artifact', v)} required
          placeholder="Posters, tri-fold boards, photos, something you built, video, an infographic: something visitors can see or handle at your table." />
      </FormStep>

      <section className="card pform__submit">
      <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
        <button className="btn" disabled={busy || !complete} onClick={submit}>
          {busy ? 'Sending…' : `Submit to ${me.seminarTeacherName || 'your seminar teacher'}`}
        </button>
        <button className="btn btn--ghost" disabled={busy || !f.title.trim()} onClick={save}>Save draft</button>
        {existing?.status === 'draft' && (
          <button className="btn btn--ghost" disabled={busy}
            onClick={run(() => deleteDraft(term.id, me.email))}>Delete draft</button>
        )}
        {existing && <button className="linkish" disabled={busy} onClick={onDone}>Cancel</button>}
      </div>
      {!complete && (
        <p className="opt__hint">
          Before you can submit: at least one kind of project, a title, every host filled in (place, contact, phone or email, address, days{proofRequired ? ', proof they agreed' : ''}), an overview, your open-house artifact, and what and where for
          every block{gaps ? ` (${gaps} still empty)` : ''}. You can save a draft any time.
        </p>
      )}
      </section>
    </div>
  )
}

/** One numbered part of the proposal form, so the long page reads in order. */
function FormStep({ n, title, hint, required, children }: {
  n: number; title: string; hint?: string; required?: boolean; children: ReactNode
}) {
  return (
    <section className="card pform__step" aria-labelledby={`pform-${n}`}>
      <header className="pform__head">
        <span className="pform__n" aria-hidden="true">{n}</span>
        <div>
          <h2 id={`pform-${n}`} className="pform__title">{title}{required && <span className="req"> *</span>}</h2>
          {hint && <p className="pform__hint">{hint}</p>}
        </div>
      </header>
      <div className="pform__body">{children}</div>
    </section>
  )
}

/**
 * The project's week on the real bell schedule: what the student will do and
 * where, for every block. A project student is out all day, so the office and
 * the teacher doing site visits need a place for each block, not a paragraph.
 */
function ProjectPlan({ term, plan, hosts, onChange }: {
  term: NtTerm; plan: NtProjectBlock[]; hosts: NtHost[]; onChange: (p: NtProjectBlock[]) => void
}) {
  const slots = projectSlots(term)
  const placeOf = (h: NtHost) => [h.org.trim(), h.address.trim()].filter(Boolean).join(', ')
  // The host booked for that day, offered so the student does not type the address six times.
  const hostFor = (day: number) => hosts.find((h) => h.days.includes(day) && placeOf(h))
  const find = (sl: (typeof slots)[number]) => plan.find((b) => b.day === sl.day && b.block === sl.block && b.part === sl.part)
  const set = (sl: (typeof slots)[number], patch: Partial<NtProjectBlock>) => {
    const cur = find(sl) ?? { day: sl.day, block: sl.block, part: sl.part, what: '', where: '' }
    const next = { ...cur, ...patch }
    onChange([...plan.filter((b) => b !== cur && !(b.day === sl.day && b.block === sl.block && b.part === sl.part)), next])
  }
  return (
    <section>
      <datalist id="pplan-places">
        {[...new Set(hosts.map(placeOf).filter(Boolean))].map((x) => <option key={x} value={x} />)}
      </datalist>
      {([0, 1, 2, 3] as const).map((d) => {
        const daySlots = slots.filter((x) => x.day === d)
        if (!daySlots.length) return null
        return (
          <div key={d} className="plan__day">
            <div className="plan__dayname">{DAY_LABELS[d]}{d === 3 ? ', then the open house' : ''}</div>
            {daySlots.map((sl, i) => {
              const b = find(sl)
              const prevWhere = i > 0 ? find(daySlots[i - 1]!)?.where : undefined
              return (
                <div key={`${sl.block}${sl.part}`} className="pplan__row">
                  <div className="plan__label">{sl.label} · {sl.start}–{sl.end}</div>
                  <div className="pplan__fields">
                    <input className="opt__input" aria-label={`${DAY_LABELS[d]}, ${sl.label}: what you will do`}
                      placeholder={d === 3 ? 'e.g. Finish and set up my artifact' : "What you'll do"} value={b?.what ?? ''} onChange={(e) => set(sl, { what: e.target.value })} />
                    {d === 3
                      ? <div className="pplan__fixed">At school: everyone is on campus Friday</div>
                      : <input className="opt__input" aria-label={`${DAY_LABELS[d]}, ${sl.label}: where`}
                          list="pplan-places" placeholder="Where (place and address)" value={b?.where ?? ''} onChange={(e) => set(sl, { where: e.target.value })} />}
                  </div>
                  {d !== 3 && !b?.where && (prevWhere
                    ? <button type="button" className="linkish pplan__same" onClick={() => set(sl, { where: prevWhere })}>
                        Same place as the block before
                      </button>
                    : hostFor(d) && <button type="button" className="linkish pplan__same" onClick={() => set(sl, { where: placeOf(hostFor(d)!) })}>
                        At {hostFor(d)!.org.trim() || 'your host'}
                      </button>)}
                </div>
              )
            })}
          </div>
        )
      })}
    </section>
  )
}

/** The plan, read-only, for whoever reviews the proposal. */
function ProjectPlanView({ plan }: { plan: NtProjectBlock[] }) {
  const { term } = useNorseTerm()
  const slots = projectSlots(term)
  return (
    <div style={{ margin: 'var(--s-3) 0' }}>
      <div className="opt__label">The week</div>
      <ul className="pplan__view">
        {slots.map((sl) => {
          const b = plan.find((x) => x.day === sl.day && x.block === sl.block && x.part === sl.part)
          return (
            <li key={`${sl.day}${sl.block}${sl.part}`}>
              <span className="pplan__when">{DAY_LABELS[sl.day].slice(0, 3)} · {sl.label}</span>
              <span>{b?.what || <em className="work__sub">Not planned</em>}</span>
              <span className="work__sub">{sl.day === 3 ? `@ ${FRIDAY_WHERE}` : b?.where ? `@ ${b.where}` : ''}</span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * Proof a host agreed: a screenshot or PDF of a confirmation email, a
 * signed form. Uploaded straight to Storage (images and PDFs, under 10 MB,
 * only into the student's own folder: storage.rules) and linked on the
 * proposal, which saves with the rest of the form. Required to submit a job
 * shadow or college visits; optional otherwise.
 */
function ProofUpload({ termId, email, proofs, required, room, onChange }: {
  termId: string; email: string; proofs: NtProof[]; required: boolean
  /** How many more files the whole proposal may hold. */
  room: number
  onChange: (p: NtProof[]) => void
}) {
  const preview = !!useAuth().preview
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    if (files.length > room) { setErr(room ? `You can add ${room} more file${room === 1 ? '' : 's'}.` : 'That is the most files a proposal can hold. Remove one first.'); return }
    setBusy(true); setErr(null)
    try {
      const added: NtProof[] = []
      for (const file of Array.from(files)) {
        if (!/^image\/|^application\/pdf$/.test(file.type)) throw new Error(`${file.name} is not an image or a PDF.`)
        if (file.size >= 10 * 1024 * 1024) throw new Error(`${file.name} is over 10 MB.`)
        const path = `proposals/${termId}/${email.toLowerCase()}/${Date.now()}-${file.name.replace(/[^\w.-]+/g, '_')}`
        const r = storageRef(storage, path)
        await uploadBytes(r, file, { contentType: file.type })
        added.push({ name: file.name, url: await getDownloadURL(r), path, contentType: file.type, size: file.size })
      }
      onChange([...proofs, ...added])
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }
  const remove = async (f: NtProof) => {
    onChange(proofs.filter((x) => x.path !== f.path))
    await deleteObject(storageRef(storage, f.path)).catch(() => {})
  }
  return (
    <div style={{ marginTop: 'var(--s-2)' }}>
      <div className="opt__label" style={{ margin: 0 }}>
        Proof they agreed{required ? <span className="req"> *</span> : ' (optional)'}
      </div>
      <p className="opt__hint" style={{ margin: '2px 0 var(--s-2)' }}>
        A screenshot or PDF of the email or form where they said yes{required ? '. Needed for a job shadow or college visit' : ''}.
      </p>
      {err && <ErrorState body={err} />}
      {proofs.length > 0 && (
        <ul className="proof__list">
          {proofs.map((f) => (
            <li key={f.path}>
              <a href={f.url} target="_blank" rel="noreferrer">{f.name}</a>
              <button type="button" className="linkish" onClick={() => remove(f)} aria-label={`Remove ${f.name}`}>Remove</button>
            </li>
          ))}
        </ul>
      )}
      <label className="plan__add" style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: preview ? 'not-allowed' : 'pointer' }}>
        {busy ? 'Uploading…' : preview ? 'Uploading is off while previewing' : '+ Upload a screenshot or PDF'}
        <input type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} disabled={busy || preview}
          onChange={(e) => { void upload(e.target.files); e.target.value = '' }} />
      </label>
    </div>
  )
}

/**
 * Everyone who agreed to host the student, one card each: the place, a
 * person to call, how to reach them, the address, which days, and proof they
 * said yes. Split up so a reviewer can check it and the teacher doing a site
 * visit can find it, which one paragraph never managed.
 */
function HostsEditor({ termId, email, hosts, proofRequired, legacy, onChange }: {
  termId: string; email: string; hosts: NtHost[]; proofRequired: boolean
  /** What the student typed before hosts were split up, shown so it can be copied over. */
  legacy?: string
  onChange: (h: NtHost[]) => void
}) {
  const patch = (i: number, p: Partial<NtHost>) => onChange(hosts.map((h, j) => (j === i ? { ...h, ...p } : h)))
  const room = 10 - hosts.reduce((n, h) => n + h.proofs.length, 0)
  const remove = (i: number) => {
    for (const f of hosts[i]!.proofs) void deleteObject(storageRef(storage, f.path)).catch(() => {})
    onChange(hosts.filter((_, j) => j !== i))
  }
  return (
    <section>
      {legacy?.trim() && (
        <div className="notice notice--info" style={{ marginBottom: 'var(--s-3)' }}>
          <span aria-hidden="true">i</span>
          <div>You wrote earlier: <span style={{ whiteSpace: 'pre-wrap' }}>{legacy}</span>. Put it in the boxes below.</div>
        </div>
      )}
      {hosts.map((h, i) => {
        const missing = hostGaps(h, proofRequired)
        return (
          <div key={i} className="host__card">
            <div className="host__head">
              <strong>{hosts.length > 1 ? `Host ${i + 1}` : 'Your host'}{h.org.trim() ? `: ${h.org.trim()}` : ''}</strong>
              {hosts.length > 1 && <button type="button" className="linkish" onClick={() => remove(i)}>Remove</button>}
            </div>
            <Text label="Place or organization" value={h.org} onChange={(v) => patch(i, { org: v })} required
              placeholder="Story City Veterinary Clinic, Iowa State University" />
            <div className="host__grid">
              <Text label="Contact person" value={h.name} onChange={(v) => patch(i, { name: v })} required placeholder="Dr. Anna Lee" />
              <Text label="Their job or role" value={h.role} onChange={(v) => patch(i, { role: v })} placeholder="Veterinarian, admissions counselor" />
              <Text label="Phone" type="tel" value={h.phone} onChange={(v) => patch(i, { phone: v })} placeholder="515-555-0100" />
              <Text label="Email" type="email" value={h.email} onChange={(v) => patch(i, { email: v })} placeholder="alee@example.com" />
            </div>
            <p className="opt__hint" style={{ margin: '-8px 0 var(--s-3)' }}>A phone or an email, so school can reach them.</p>
            <Text label="Address" value={h.address} onChange={(v) => patch(i, { address: v })} required
              placeholder="512 Broad St, Story City, IA 50248" />
            <div className="opt__label" style={{ margin: 0 }}>Which days you&apos;ll be there <span className="req">*</span></div>
            <div className="chips" style={{ margin: '4px 0 0' }}>
              {[0, 1, 2].map((d) => {
                const on = h.days.includes(d)
                return (
                  <button key={d} type="button" aria-pressed={on} className={`chip${on ? ' is-on' : ''}`}
                    onClick={() => patch(i, { days: on ? h.days.filter((x) => x !== d) : [...h.days, d].sort() })}>
                    {DAY_LABELS[d]}
                  </button>
                )
              })}
            </div>
            <ProofUpload termId={termId} email={email} proofs={h.proofs} required={proofRequired} room={room}
              onChange={(proofs) => patch(i, { proofs })} />
            {missing.length > 0 && <p className="opt__hint" style={{ margin: 0 }}>Still needs {missing.join(', ')}.</p>}
          </div>
        )
      })}
      {hosts.length < 6 && (
        <button type="button" className="plan__add" onClick={() => onChange([...hosts, BLANK_HOST()])}>
          + Add another host (a different place on another day)
        </button>
      )}
    </section>
  )
}

/** One host as a reviewer sees it: who to call and where the student will be. */
function HostView({ h }: { h: NtHost }) {
  return (
    <div className="host__view">
      <div style={{ fontWeight: 650 }}>{h.org || 'Place not named'}</div>
      <div className="work__sub">{h.days.length ? h.days.map((d) => DAY_LABELS[d]).join(', ') : 'Days not given'}</div>
      <dl className="host__dl">
        <dt>Contact</dt><dd>{[h.name, h.role].filter(Boolean).join(', ') || 'Not given'}</dd>
        {h.phone && <><dt>Phone</dt><dd><a href={`tel:${h.phone}`}>{h.phone}</a></dd></>}
        {h.email && <><dt>Email</dt><dd><a href={`mailto:${h.email}`}>{h.email}</a></dd></>}
        <dt>Address</dt><dd>{h.address || 'Not given'}</dd>
        <dt>Proof</dt>
        <dd>{h.proofs.length ? h.proofs.map((f, j) => <span key={f.path}>{j > 0 && ', '}<a href={f.url} target="_blank" rel="noreferrer">{f.name}</a></span>) : 'None attached'}</dd>
      </dl>
    </div>
  )
}

/* ---------------- shared pieces ---------------- */

function Section<T extends { id: string }>({ title, items, empty, hint, render }: {
  title: string
  items: T[]
  empty?: string
  hint?: string
  render: (t: T) => React.ReactNode
}) {
  return (
    <>
      <h2 className="work__heading">
        {title} {items.length > 0 && <span className="work__badge">{items.length}</span>}
      </h2>
      {hint && items.length > 0 && <p className="opt__hint" style={{ marginTop: 0 }}>{hint}</p>}
      {items.length === 0 && empty ? <Empty title="Nothing here" body={empty} /> : items.map(render)}
    </>
  )
}

/** Write → seminar teacher → admin → approved. Colour never carries it alone. */
function Track({ status }: { status: NtProposal['status'] }) {
  const steps = ['Written', 'Seminar teacher', 'Admin', 'Approved']
  const at = { draft: 0, returned: 0, submitted: 1, checked: 2, approved: 4 }[status]
  return (
    <ol className="ptrack" aria-label="Where your proposal is">
      {steps.map((s, i) => {
        const state = i < at ? 'done' : i === at ? 'now' : 'todo'
        const Icon = state === 'done' ? Check : state === 'now' ? CircleDot : Circle
        return (
          <li key={s} className={`ptrack__step is-${state}`}>
            <Icon size={14} aria-hidden="true" /> {s}
            <span className="visually-hidden">{state === 'done' ? ' (done)' : state === 'now' ? ' (current)' : ''}</span>
          </li>
        )
      })}
    </ol>
  )
}

function ProposalCard({ p, all, viewer, me, canAct, collapsed = false }: {
  p: NtProposal
  /** Every proposal, for matching the partners a student named (admin and office). */
  all?: NtProposal[]
  viewer: 'student' | 'seminar' | 'decider'
  me: Me
  canAct: boolean
  collapsed?: boolean
}) {
  const [open, setOpen] = useState(!collapsed)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const events = useProposalEvents(open ? p.id : undefined)

  const act = (status: NtProposal['status'], action: 'checked' | 'returned' | 'approved') => async () => {
    if (action === 'returned' && !note.trim()) {
      setErr('Say what has to change. That is the whole point of sending it back.'); return
    }
    setBusy(true); setErr(null)
    try {
      await moveProposal(p.id, {
        status, feedback: note.trim(),
        ...(viewer === 'seminar' ? { checkedByName: me.name } : { decidedByName: me.name }),
      }, { byEmail: me.email, byName: me.name, action, note: note.trim() })
      setNote('')
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)) }
    finally { setBusy(false) }
  }

  const statusPill = p.status === 'approved' ? 'pill--good' : p.status === 'returned' ? 'pill--serious' : ''

  return (
    <section className={`card${canAct && open ? ' card--action' : ''}`} style={{ marginTop: 'var(--s-2)' }}>
      <button className="cat__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div>
          <div style={{ fontWeight: 620 }}>{p.title || 'Untitled'}</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 2 }}>
            {viewer !== 'student' && <>{p.studentName} · </>}{proposalTypes(p).map(typeLabel).join(' + ')}
          </div>
        </div>
        <span className={`pill ${statusPill}`}>{proposalWhere(p, viewer)}</span>
      </button>

      {open && (
        <div style={{ marginTop: 'var(--s-3)' }}>
          {/* The single most common reason these get returned, so it is not
              buried in the paragraph. */}
          {(p.hosts ?? []).length > 0 ? (
            <div style={{ margin: 'var(--s-2) 0' }}>
              <div className="opt__label">{p.hosts!.length > 1 ? 'Hosts' : 'Host'}</div>
              {p.hosts!.map((h, i) => <HostView key={i} h={h} />)}
            </div>
          ) : (
            <div className={`notice ${p.host?.trim() ? 'notice--info' : 'notice--warning'}`}>
              <span aria-hidden="true">{p.host?.trim() ? 'i' : '⚠'}</span>
              <div>{p.host?.trim() ? <><strong>Host:</strong> <span style={{ whiteSpace: 'pre-wrap' }}>{p.host}</span></> : 'No named host who has agreed.'}</div>
            </div>
          )}
          <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>{p.detail}</p>
          {!(p.hosts ?? []).length && (p.proofs ?? []).length > 0 && (
            <div style={{ margin: 'var(--s-2) 0' }}>
              <div className="opt__label">Proof the host agreed</div>
              <ul className="proof__list">
                {p.proofs!.map((f) => <li key={f.path}><a href={f.url} target="_blank" rel="noreferrer">{f.name}</a></li>)}
              </ul>
            </div>
          )}
          {needsProof(proposalTypes(p)) && !(p.proofs ?? []).length && (
            <div className="notice notice--warning"><span aria-hidden="true">⚠</span><div>No proof that the host agreed is attached.</div></div>
          )}
          {viewer === 'decider' && all && (p.partnersText?.trim() || (p.partnerEmails ?? []).length > 0) && <PartnerLinker p={p} all={all} />}
          {(p.plan ?? []).length > 0 && <ProjectPlanView plan={p.plan!} />}
          {p.artifact?.trim() && (
            <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>
              <strong>Open-house artifact:</strong> {p.artifact}
            </p>
          )}

          {events.length > 0 && (
            <div style={{ margin: 'var(--s-3) 0' }}>
              <div className="opt__label">History</div>
              <ol className="phist">
                {events.map((e) => (
                  <li key={e.id}>
                    <strong>{e.byName}</strong> {ACTION_WORDS[e.action]}
                    {fmtWhen(e.at) && <span className="phist__when"> · {fmtWhen(e.at)}</span>}
                    {e.note && <div className="phist__note">“{e.note}”</div>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {canAct && (
            <>
              {err && <ErrorState body={err} />}
              <Area label="Note to the student (required to send back)" value={note} onChange={setNote}
                placeholder="Who is supervising you, and what exists on Friday that does not exist now?" />
              <div style={{ display: 'flex', gap: 'var(--s-2)', flexWrap: 'wrap' }}>
                {viewer === 'seminar' && p.status === 'submitted' && (
                  <button className="btn" onClick={act('checked', 'checked')} disabled={busy}>Check it off</button>
                )}
                {viewer === 'decider' && (p.status === 'submitted' || p.status === 'checked') && (
                  <button className="btn" onClick={act('approved', 'approved')} disabled={busy}>Approve</button>
                )}
                <button className="btn btn--ghost" onClick={act('returned', 'returned')} disabled={busy}>
                  {p.status === 'approved' ? 'Reopen and send back' : 'Send back'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * Project exemplars for students to browse — beside their own proposal, not
 * under anyone's review queue. Filterable by kind of project, because a
 * student planning a job shadow wants to see job shadows.
 */
/** The examples page for students: every example with its reason showing, strong first. */
export function ProjectExamples() {
  return (
    <>
      <p className="ov__p" style={{ marginTop: 'var(--s-2)' }}>
        Strong proposals to learn from, and ones that would be sent back. The reason under each is the useful part:
        it is what your seminar teacher and the office check.
      </p>
      <Exemplars full />
    </>
  )
}

function Exemplars({ full = false }: { full?: boolean }) {
  const exemplars = useExemplars()
  const [kind, setKind] = useState<NtProposal['type'] | 'all'>('all')
  const kinds = PROJECT_TYPES.filter((t) => exemplars.some((e) => e.type === t.k))
  const shown = exemplars.filter((e) => kind === 'all' || e.type === kind)
    .sort((a, b) => Number(b.good) - Number(a.good))
  return (
    <>
      {!full && <h2 className="work__heading">Example projects {exemplars.length > 0 && <span className="work__badge">{exemplars.length}</span>}</h2>}
      {exemplars.length === 0 ? (
        <p className="opt__hint">
          No examples saved yet. The best ones are last year's. Whoever manages Norse Term can add them.
        </p>
      ) : (
        <>
          {!full && <p className="opt__hint" style={{ marginTop: 0 }}>
            Strong ones to learn from, and ones that would be sent back, with the reason, which is the useful part.
            {' '}<Link to="/norseterm/examples">See them all with their reasons →</Link>
          </p>}
          {kinds.length > 1 && (
            <div className="chips" style={{ margin: 'var(--s-2) 0' }}>
              {[{ k: 'all' as const, label: 'All' }, ...kinds].map((t) => (
                <button key={t.k} className={`chip${kind === t.k ? ' is-on' : ''}`} onClick={() => setKind(t.k)}>{t.label}</button>
              ))}
            </div>
          )}
          {shown.map((e) => <ExemplarCard key={e.id} e={e} full={full} />)}
        </>
      )}
    </>
  )
}

function ExemplarCard({ e, full = false }: { e: NtExemplar; full?: boolean }) {
  const [open, setOpen] = useState(false)
  if (full) {
    return (
      <section className={`card ex__card ${e.good ? 'is-good' : 'is-weak'}`} style={{ marginTop: 'var(--s-3)' }}>
        <div className="cat__head" style={{ cursor: 'default' }}>
          <div>
            <h2 className="ov__h" style={{ margin: 0 }}>{e.title}</h2>
            <div className="work__sub">{typeLabel(e.type)}</div>
          </div>
          <span className={`pill ${e.good ? 'pill--good' : 'pill--serious'}`}>
            {e.good ? <><Check size={12} aria-hidden="true" /> Strong</> : <><CornerUpLeft size={12} aria-hidden="true" /> Would be sent back</>}
          </span>
        </div>
        <p className="ov__p" style={{ margin: 'var(--s-2) 0 0' }}><strong>{e.good ? 'Why it works: ' : 'Why it would be sent back: '}</strong>{e.why}</p>
        <button className="linkish" style={{ marginTop: 'var(--s-2)' }} aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide the proposal' : 'Read the proposal'}
        </button>
        {open && <p className="ex__detail">{e.detail}</p>}
      </section>
    )
  }
  return (
    <section className="card" style={{ marginTop: 'var(--s-2)' }}>
      <button className="cat__head" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div>
          <div style={{ fontWeight: 620 }}>{e.title}</div>
          <div style={{ fontSize: 'var(--t-xs)', color: 'var(--ink-muted)', marginTop: 2 }}>
            {typeLabel(e.type)}
          </div>
        </div>
        <span className={`pill ${e.good ? 'pill--good' : 'pill--serious'}`}>
          {e.good ? 'Strong' : 'Would be sent back'}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 'var(--s-3)' }}>
          <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)' }}><strong>Why:</strong> {e.why}</p>
          <p style={{ fontSize: 'var(--t-sm)', color: 'var(--ink-2)', whiteSpace: 'pre-wrap' }}>{e.detail}</p>
        </div>
      )}
    </section>
  )
}

const ACTION_WORDS = {
  submitted: 'submitted it',
  checked: 'checked it off',
  returned: 'sent it back',
  approved: 'approved it',
} as const

const typeLabel = (t: NtProposal['type']) =>
  PROJECT_TYPES.find((x) => x.k === t)?.label ?? t

function fmtWhen(v: unknown): string {
  const d = v && typeof (v as { toDate?: unknown }).toDate === 'function' ? (v as { toDate: () => Date }).toDate() : null
  return d ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''
}

function fmtDate(v: string): string {
  const d = new Date(v + 'T12:00:00')
  return isNaN(d.getTime()) ? v : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * A joint project: the student typed their partners' names; admin or office
 * picks the real students, which joins the projects for check-ins. Linked on
 * both proposals at once.
 */
function PartnerLinker({ p, all }: { p: NtProposal; all: NtProposal[] }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const linked = all.filter((x) => (p.partnerEmails ?? []).includes(x.studentEmail) || (x.partnerEmails ?? []).includes(p.studentEmail))
  const typed = (p.partnersText ?? '').toLowerCase()
  // Students whose names the typed text mentions come first.
  const others = all.filter((x) => x.id !== p.id && !linked.includes(x))
    .sort((a, b) => Number(typed.includes(b.studentName.toLowerCase().split(' ')[0] ?? '~')) - Number(typed.includes(a.studentName.toLowerCase().split(' ')[0] ?? '~'))
      || a.studentName.localeCompare(b.studentName))
  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true); setErr(null)
    try { await fn() } catch (e) { setErr(e instanceof Error ? e.message : String(e)) } finally { setBusy(false) }
  }
  return (
    <div className="notice notice--info" style={{ display: 'block' }}>
      {err && <ErrorState body={`Not saved. ${err}`} />}
      <div><strong>Joint project.</strong>{p.partnersText?.trim() ? <> The student named: <em>{p.partnersText}</em></> : null}</div>
      {linked.length > 0 && (
        <div style={{ marginTop: 4 }}>Joined with {linked.map((x, i) => (
          <span key={x.id}>{i > 0 && ', '}<strong>{x.studentName}</strong> <button className="linkish" disabled={busy} onClick={run(() => linkPartners(p, x, false))}>undo</button></span>
        ))}</div>
      )}
      <label style={{ display: 'block', marginTop: 'var(--s-2)' }}>
        <span className="opt__label" style={{ margin: 0 }}>Join with</span>
        <select className="opt__input" style={{ width: '100%', marginTop: 4 }} value="" disabled={busy}
          onChange={(e) => { const x = others.find((o) => o.id === e.target.value); if (x) void run(() => linkPartners(p, x, true))() }}>
          <option value="">Choose the student…</option>
          {others.map((x) => <option key={x.id} value={x.id}>{x.studentName}{x.status !== 'approved' ? ` (${x.status})` : ''}</option>)}
        </select>
      </label>
    </div>
  )
}
